<template lang="pug">
div.kpi
    //- Toolbar: title, period, actions.
    v-card.mb-3(flat)
        .d-flex.align-center.flex-wrap.px-4.py-2
            .mr-4.my-1
                .text-subtitle-1.font-weight-medium KPI Reports
                .text-caption.grey--text(v-if="report")
                    | {{ rangeLabel(report.period.start, report.period.end) }} vs {{ rangeLabel(report.period.previousStart, report.period.previousEnd) }} (UTC)
            v-spacer
            v-chip-group.mr-2(v-model="preset" mandatory active-class="primary white--text")
                v-chip(v-for="p in presets" :key="p.value" :value="p.value" small label) {{ p.text }}
            date-range-picker.mr-2(
                v-if="preset === 'Custom'"
                v-model="customRange"
                style="font-size: 13px; border-bottom: 1px solid #25252575; max-width: 200px;"
            )
            v-btn(icon small :disabled="!report" @click="exportCsv" title="Export CSV")
                v-icon(small) mdi-download
            v-btn(icon small :loading="loading" @click="load" title="Refresh")
                v-icon(small) mdi-refresh
        v-progress-linear(v-if="loading" indeterminate color="primary" height="2")

    v-alert(v-if="error" type="error" text dense dismissible @input="error = null") {{ error }}

    template(v-if="report")
        //- Headline numbers.
        v-row(dense)
            v-col(v-for="def in headline" :key="def.key" cols="6" md="3")
                v-card.kpi-stat(
                    flat
                    :style="def.key === selectedKey ? activeStyle : {}"
                    @click="selectedKey = def.key"
                )
                    .kpi-label
                        | {{ def.label }}
                        v-tooltip(bottom max-width="300")
                            template(v-slot:activator="{ on, attrs }")
                                v-icon.ml-1(x-small v-bind="attrs" v-on="on") mdi-information-outline
                            span {{ def.definition }}
                    .kpi-big {{ fmt(def, report.current) }}
                    .d-flex.align-center
                        span.kpi-delta(v-if="change(def)" :class="deltaClass(def)") {{ deltaText(def) }}
                        span.kpi-muted.ml-2 {{ def.detail ? def.detail(report.current) : 'vs previous period' }}

        //- Everything else as short lists.
        v-row(dense)
            v-col(v-for="section in sections" :key="section.title" cols="12" md="4")
                v-card.kpi-list(flat)
                    .kpi-list-title {{ section.title }}
                    .kpi-row(
                        v-for="def in section.defs"
                        :key="def.key"
                        :style="def.key === selectedKey ? activeStyle : {}"
                        @click="selectedKey = def.key"
                    )
                        .kpi-row-label
                            | {{ def.label }}
                            v-tooltip(bottom max-width="300")
                                template(v-slot:activator="{ on, attrs }")
                                    v-icon.ml-1(x-small v-bind="attrs" v-on="on") mdi-information-outline
                                span
                                    | {{ def.definition }}
                                    template(v-if="def.detail")
                                        br
                                        b {{ def.detail(report.current) }}
                        .kpi-row-value {{ fmt(def, report.current) }}
                        .kpi-row-delta
                            span.kpi-delta(v-if="change(def)" :class="deltaClass(def)") {{ deltaText(def) }}

        //- Drop-off funnels (recorded steps only).
        v-card.mt-3(v-if="report.current.ratingFunnel" flat)
            .kpi-list-title.pt-3
                | Drop-off funnels
                span.kpi-muted.ml-2 partial: starts at the first step the app records
            v-row(no-gutters)
                v-col(v-for="f in funnels" :key="f.title" cols="12" md="6")
                    .px-4.pb-3
                        .kpi-row-label.mb-1
                            span.font-weight-medium {{ f.title }}
                            span.kpi-muted.ml-1 · {{ f.subtitle }}
                        .kpi-step(
                            v-for="(step, i) in f.steps"
                            :key="step.label"
                            :style="step.rateKey === selectedKey ? activeStyle : {}"
                            @click="step.rateKey && (selectedKey = step.rateKey)"
                        )
                            .kpi-step-label {{ step.label }}
                            .kpi-step-bar
                                .kpi-step-fill(:style="{ width: stepWidth(f, step) + '%', background: primaryColor }")
                            .kpi-step-count {{ fmt({ unit: 'count', get: step.get }, report.current) }}
                            .kpi-step-rate
                                template(v-if="step.rateKey")
                                    | {{ fmt(metric(step.rateKey), report.current) }}
                                    span.kpi-delta.ml-1(v-if="change(metric(step.rateKey))" :class="deltaClass(metric(step.rateKey))") {{ deltaText(metric(step.rateKey)) }}
                                span.kpi-muted(v-else) 100%

        .kpi-muted.px-1.mt-2
            v-icon.mr-1(x-small) mdi-eye-off-outline
            | Not tracked by the app yet: {{ notTracked.map(d => d.label).join(' · ') }}.
            | New users, ratings and song uploads are on the&nbsp;
            router-link(to="/dashboard") Dashboard
            | .

        //- Trend of the selected metric.
        v-card.mt-3(flat)
            .d-flex.align-center.flex-wrap.px-4.pt-3
                .text-subtitle-2 {{ selectedDef.label }}
                v-spacer
                v-select.mr-2(
                    v-model="selectedKey"
                    :items="chartable"
                    item-text="label"
                    item-value="key"
                    dense
                    outlined
                    hide-details
                    style="max-width: 230px;"
                )
                v-btn-toggle.mr-2(v-model="granularity" mandatory dense color="primary")
                    v-btn(x-small value="day") Day
                    v-btn(x-small value="week") Week
                    v-btn(x-small value="month") Month
                v-btn-toggle(v-model="view" mandatory dense color="primary")
                    v-btn(x-small value="chart" title="Chart")
                        v-icon(small) mdi-chart-line
                    v-btn(x-small value="table" title="Table")
                        v-icon(small) mdi-table
            v-card-text
                line-chart(v-if="view === 'chart'" :chart-data="chartData" :options="chartOptions" style="height: 260px;")
                v-data-table(v-else :headers="tableHeaders" :items="tableRows" dense :items-per-page="10")

        v-expansion-panels.mt-3(flat)
            v-expansion-panel
                v-expansion-panel-header.text-subtitle-2 How each metric is calculated
                v-expansion-panel-content
                    v-simple-table(dense)
                        template(v-slot:default)
                            tbody
                                tr(v-for="def in allDefs" :key="def.key")
                                    td.font-weight-medium(style="width: 220px;") {{ def.label }}
                                    td {{ def.notTracked ? 'Not tracked: ' + def.notTracked : def.definition }}
</template>

<script>
import moment from 'moment';
import feathersClient from '@/plugins/feathers-client';
import LineChart from '../Dashboard/components/LineChart';
import { METRICS, HEADLINE, SECTIONS, NOT_TRACKED, FUNNELS, formatValue, changeOf } from './definitions';

const PRESETS = [
    { value: 'Last 7 Days', text: '7D' },
    { value: 'Last 30 Days', text: '30D' },
    { value: 'This Month', text: 'This month' },
    { value: 'Last Month', text: 'Last month' },
    { value: 'Last 3 Months', text: '3M' },
    { value: 'Last 6 Months', text: '6M' },
    { value: 'Last 12 Months', text: '12M' },
    { value: 'Custom', text: 'Custom' }
];

const withKey = k => ({ key: k, ...METRICS[k] });
const DEFS = Object.keys(METRICS).map(withKey);

export default {
    name: 'KpiDashboard',
    components: { LineChart },
    data() {
        return {
            presets: PRESETS,
            preset: 'Last 30 Days',
            customRange: [],
            granularity: 'day',
            view: 'chart',
            report: null,
            loading: false,
            error: null,
            selectedKey: 'activeUsers'
        };
    },
    computed: {
        headline() {
            return HEADLINE.map(withKey);
        },
        sections() {
            return SECTIONS.map(s => ({ title: s.title, defs: s.keys.map(withKey) }));
        },
        notTracked() {
            return NOT_TRACKED.map(withKey);
        },
        funnels() {
            return FUNNELS;
        },
        primaryColor() {
            return this.$vuetify.theme.currentTheme.primary;
        },
        allDefs() {
            return DEFS;
        },
        chartable() {
            return DEFS.filter(d => !d.notTracked);
        },
        selectedDef() {
            return withKey(this.selectedKey);
        },
        // The CMS's own primary colour marks the metric shown in the chart.
        activeStyle() {
            return { boxShadow: `inset 3px 0 0 ${this.$vuetify.theme.currentTheme.primary}` };
        },
        // UTC dates in the API's DD/MM/YYYY format.
        dateRanged() {
            const t = moment.utc();
            const f = d => d.format('DD/MM/YYYY');
            switch (this.preset) {
                case 'Last 7 Days': return [f(t.clone().subtract(6, 'days')), f(t)];
                case 'Last 30 Days': return [f(t.clone().subtract(29, 'days')), f(t)];
                case 'This Month': return [f(t.clone().startOf('month')), f(t)];
                case 'Last Month': {
                    const m = t.clone().subtract(1, 'months');
                    return [f(m.clone().startOf('month')), f(m.clone().endOf('month'))];
                }
                case 'Last 3 Months': return [f(t.clone().subtract(3, 'months')), f(t)];
                case 'Last 6 Months': return [f(t.clone().subtract(6, 'months')), f(t)];
                case 'Last 12 Months': return [f(t.clone().subtract(12, 'months')), f(t)];
                default: return this.customRange && this.customRange.length === 2 ? this.customRange : null;
            }
        },
        chartOptions() {
            return {
                responsive: true,
                maintainAspectRatio: false,
                legend: { display: false },
                tooltips: { mode: 'index', intersect: false },
                hover: { mode: 'index', intersect: false },
                elements: { point: { radius: 2 } },
                scales: {
                    yAxes: [{ ticks: { beginAtZero: true, maxTicksLimit: 5 }, gridLines: { drawBorder: false, color: 'rgba(0,0,0,0.06)' } }],
                    xAxes: [{ ticks: { maxTicksLimit: 10 }, gridLines: { display: false } }]
                }
            };
        },
        chartData() {
            if (!this.report) return { labels: [], datasets: [] };
            const def = this.selectedDef;
            const color = this.$vuetify.theme.currentTheme.primary;
            return {
                labels: this.report.points.map(p => this.bucketLabel(p.bucket)),
                datasets: [{
                    label: def.label,
                    borderColor: color,
                    backgroundColor: color,
                    fill: false,
                    tension: 0.1,
                    data: this.report.points.map(p => def.get(p))
                }]
            };
        },
        tableHeaders() {
            return [{ text: 'Period', value: 'bucket' }, ...this.chartable.map(d => ({ text: d.label, value: d.key }))];
        },
        tableRows() {
            if (!this.report) return [];
            return this.report.points.map(p => this.chartable.reduce(
                (row, d) => ({ ...row, [d.key]: formatValue(d.unit, d.get(p)) }),
                { bucket: this.bucketLabel(p.bucket) }
            ));
        }
    },
    watch: {
        preset() { this.load(); },
        customRange() { this.load(); },
        granularity() { this.load(); }
    },
    mounted() {
        this.load();
    },
    methods: {
        async load() {
            if (!this.dateRanged) return;
            this.loading = true;
            this.error = null;
            try {
                this.report = await feathersClient.service('api/kpi').get('summary', {
                    query: { dateRanged: this.dateRanged, granularity: this.granularity }
                });
            } catch (e) {
                this.report = null;
                this.error = e && e.code === 404
                    ? 'The API has not been updated for KPI reports yet.'
                    : `Could not load KPIs: ${(e && e.message) || e}`;
            } finally {
                this.loading = false;
            }
        },
        metric(key) {
            return withKey(key);
        },
        // Bar length relative to the funnel's first step.
        stepWidth(funnel, step) {
            const first = funnel.steps[0].get(this.report.current);
            return first ? Math.max((step.get(this.report.current) / first) * 100, 1) : 0;
        },
        fmt(def, m) {
            return formatValue(def.unit, def.get(m));
        },
        change(def) {
            return changeOf(def, def.get(this.report.current), def.get(this.report.previous));
        },
        deltaText(def) {
            const c = this.change(def);
            if (!c) return '';
            const arrow = c.value > 0 ? '▲' : c.value < 0 ? '▼' : '';
            const sign = c.value > 0 ? '+' : '';
            return `${arrow} ${sign}${c.value}${c.kind === 'pts' ? ' pts' : '%'}`.trim();
        },
        deltaClass(def) {
            const c = this.change(def);
            if (!c || c.value === 0) return 'kpi-delta--flat';
            const good = def.better === 'down' ? c.value < 0 : c.value > 0;
            return good ? 'kpi-delta--good success--text text--darken-1' : 'kpi-delta--bad error--text text--darken-1';
        },
        rangeLabel(a, b) {
            const s = moment.utc(a), e = moment.utc(b);
            return a === b ? s.format('D MMM YYYY') : `${s.format('D MMM')} – ${e.format('D MMM YYYY')}`;
        },
        bucketLabel(bucket) {
            const d = moment.utc(bucket, 'YYYY-MM-DD');
            const g = this.report.period.granularity;
            if (g === 'month') return d.format('MMM YYYY');
            if (g === 'week') return 'Wk ' + d.format('D MMM');
            return d.format('D MMM');
        },
        exportCsv() {
            const esc = v => {
                const s = v === null || v === undefined ? '' : String(v);
                return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
            };
            const r = this.report;
            const rows = [
                ['KPI report', `${r.period.start} to ${r.period.end}`, `previous ${r.period.previousStart} to ${r.period.previousEnd}`, r.period.timezone],
                [],
                ['Metric', 'Current', 'Previous', 'Change', 'Definition'],
                ...DEFS.map(d => d.notTracked
                    ? [d.label, 'not tracked', '', '', d.notTracked]
                    : [d.label, d.get(r.current), d.get(r.previous), this.deltaText(d) || 'no comparison', d.definition]),
                [],
                [`Bucket start (${r.period.granularity})`, ...this.chartable.map(d => d.label)],
                ...r.points.map(p => [p.bucket, ...this.chartable.map(d => d.get(p))])
            ];
            const csv = rows.map(row => row.map(esc).join(',')).join('\r\n');
            const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `kpi_${r.period.start}_${r.period.end}_${r.period.granularity}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
        }
    }
};
</script>

<style scoped>
.kpi-stat {
    padding: 14px 16px;
    cursor: pointer;
    height: 100%;
}
.kpi-label,
.kpi-row-label {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.6);
}
.kpi-big {
    font-size: 28px;
    font-weight: 500;
    line-height: 1.4;
    color: rgba(0, 0, 0, 0.87);
}
.kpi-list {
    height: 100%;
    padding: 8px 0;
}
.kpi-list-title {
    font-size: 13px;
    font-weight: 500;
    padding: 4px 16px 6px;
    color: rgba(0, 0, 0, 0.87);
}
.kpi-row {
    display: flex;
    align-items: center;
    padding: 7px 16px;
    cursor: pointer;
    border-top: 1px solid rgba(0, 0, 0, 0.05);
}
.kpi-row:hover {
    background: #f7f7fa;
}
.kpi-row-label {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.75);
}
.kpi-row-value {
    font-size: 14px;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.87);
    text-align: right;
}
.kpi-row-delta {
    width: 84px;
    text-align: right;
}
.kpi-step {
    display: flex;
    align-items: center;
    padding: 5px 4px;
    font-size: 13px;
    border-radius: 4px;
}
.kpi-step:hover {
    background: #f7f7fa;
}
.kpi-step-label {
    width: 150px;
    color: rgba(0, 0, 0, 0.75);
}
.kpi-step-bar {
    flex: 1;
    height: 8px;
    margin: 0 10px;
    border-radius: 4px;
    background: rgba(0, 0, 0, 0.06);
    overflow: hidden;
}
.kpi-step-fill {
    height: 100%;
    border-radius: 4px;
    opacity: 0.8;
}
.kpi-step-count {
    width: 56px;
    text-align: right;
    font-weight: 500;
}
.kpi-step-rate {
    width: 150px;
    text-align: right;
    white-space: nowrap;
}
.kpi-muted {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.5);
}
.kpi-delta {
    font-size: 11px;
    font-weight: 500;
    padding: 1px 6px;
    border-radius: 4px;
    white-space: nowrap;
}
.kpi-delta--good {
    background: rgba(76, 175, 80, 0.12);
}
.kpi-delta--bad {
    background: rgba(255, 82, 82, 0.12);
}
.kpi-delta--flat {
    color: rgba(0, 0, 0, 0.5);
    background: rgba(0, 0, 0, 0.06);
}
</style>
